import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyProfile,
  getPushSubscriptionStatus,
  subscribePushNotification,
  unsubscribePushNotification,
  updateMyProfile,
  withdrawMe,
  type AppLocale,
  type MemberSummary,
  type PushSubscriptionPayload,
  type ThemeMode,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

const profileStaleTime = 10 * 60 * 1000;

type UpdateMyProfileInput = {
  nickname?: string;
  themeMode?: ThemeMode;
  locale?: AppLocale;
  isBetaUser?: boolean;
};

type WithdrawMemberInput = {
  password: string;
};

export const useMyProfileQuery = (token: string | null) => {
  return useQuery<MemberSummary>({
    queryKey: queryKeys.member.me,
    queryFn: () => fetchMyProfile(token as string),
    enabled: Boolean(token),
    staleTime: profileStaleTime,
  });
};

export const useUpdateMyProfileMutation = (token: string) => {
  return useMutation<MemberSummary, Error, UpdateMyProfileInput>({
    mutationFn: (input) => updateMyProfile(token, input),
  });
};

export const useWithdrawMemberMutation = (token: string) => {
  return useMutation<string, Error, WithdrawMemberInput>({
    mutationFn: (input) => withdrawMe(token, input),
  });
};

export const useSubscribePushNotificationMutation = (token: string, projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, PushSubscriptionPayload>({
    mutationFn: (payload) => subscribePushNotification(token, projectId, payload),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptionsAll });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.push.subscriptionStatus(projectId, variables.endpoint),
      });
    },
  });
};

export const useUnsubscribePushNotificationMutation = (token: string, projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { endpoint: string }>({
    mutationFn: ({ endpoint }) => unsubscribePushNotification(token, projectId, endpoint),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptionsAll });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.push.subscriptionStatus(projectId, variables.endpoint),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.push.preferences(projectId) });
    },
  });
};

export const usePushSubscriptionStatusQuery = (token: string, projectId: string, endpoint: string) => {
  return useQuery<boolean>({
    queryKey: queryKeys.push.subscriptionStatus(projectId, endpoint),
    queryFn: () => getPushSubscriptionStatus(token, projectId, endpoint),
    enabled: Boolean(token && projectId && endpoint),
    staleTime: 30_000,
  });
};
